import { cn } from '@/lib/utils/utils';
import Link from 'next/link';
import { LucideIcon } from 'lucide-react';

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  type?: 'submit' | 'button' | 'reset';
  disabled?: boolean;
  variant?:
    | 'primary'
    | 'secondary'
    | 'success'
    | 'danger'
    | 'warning'
    | 'info'
    | 'outline';
  className?: string;
  loading?: boolean;
  href?: string | null;
  target?: string;
  icon?: LucideIcon;
  iconPosition?: 'left' | 'right';
}

const Button = ({
  type = 'button',
  disabled = false,
  variant = 'secondary',
  className = '',
  loading = false,
  href = null,
  target = '',
  icon: Icon,
  iconPosition = 'right',
  ...props
}: ButtonProps) => {
  let variantClasses = '';
  // const loadingClasses =
  //   "!text-transparent pointer-events-none after:content-[''] after:h-[1.125em] after:w-[1.125em] after:absolute after:left-[calc(50%-(1.125em*0.5))] after:top-[calc(50%-(1.125em*0.5))] after:block after:animate-spin after:border-2 after:rounded-full after:border-b-current after:border-l-current";
  const loadingClasses =
    "pointer-events-none after:content-[''] after:h-[1.125em] after:w-[1.125em] after:ml-2 after:inline-block after:animate-spin after:border-2 after:rounded-full after:border-b-current after:border-l-current";

  switch (variant) {
    case 'success':
      variantClasses = cn(
        'text-white bg-green-700',
        href ? 'hover:bg-green-800' : 'enabled:hover:bg-green-800'
      );
      break;
    case 'danger':
      variantClasses = cn(
        'text-white bg-red-700',
        href ? 'hover:bg-red-800' : 'enabled:hover:bg-red-800'
      );
      break;
    case 'warning':
      variantClasses = cn(
        'text-white bg-yellow-500',
        href ? 'hover:bg-yellow-600' : 'enabled:hover:bg-yellow-600'
      );
      break;
    case 'info':
      variantClasses = cn(
        'text-white bg-cyan-500',
        href ? 'hover:bg-cyan-600' : 'enabled:hover:bg-cyan-600'
      );
      break;
    case 'secondary':
      variantClasses = cn(
        'text-white bg-secondary',
        href ? 'hover:bg-secondary-600' : 'enabled:hover:bg-secondary-600'
      );
      break;
    case 'outline':
      variantClasses = cn(
        'text-gray-800 bg-none border border-gray-300',
        href ? 'hover:bg-gray-100' : 'enabled:hover:bg-gray-50'
      );
      break;
    case 'primary':
      variantClasses = cn(
        'text-white bg-primary',
        href ? 'hover:bg-primary' : 'enabled:hover:bg-primary'
      );
      break;
  }

  const buttonContent = (
    <>
      {Icon && iconPosition === 'left' && <Icon size={16} />}
      {props.children}
      {Icon && iconPosition === 'right' && <Icon size={16} />}
    </>
  );

  if (href) {
    return (
      <Link
        href={href}
        className={cn(
          'relative inline-flex items-center justify-center text-center gap-2 text-sm font-medium rounded-md h-8 px-4 disabled:opacity-75 disabled:cursor-not-allowed transition-all !outline-none !ring-0 leading-none',
          variantClasses,
          className,
          loading && `is-loading ${loadingClasses}`
        )}
        target={target}
      >
        {buttonContent}
      </Link>
    );
  }

  return (
    <button
      type={type}
      disabled={disabled || loading ? true : false}
      className={cn(
        'relative inline-flex items-center justify-center text-center gap-2 text-sm font-medium rounded-md h-8 px-4 disabled:opacity-75 disabled:cursor-not-allowed transition-all !outline-none !ring-0 leading-none',
        variantClasses,
        className,
        loading && `is-loading ${loadingClasses}`
      )}
      {...props}
    >
      {buttonContent}
    </button>
  );
};

export default Button;
